import React from "react";
import { graphql, useFragment } from "react-relay";

import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";
import { Node as ProseNode } from "prosemirror-model";

import NoteCheckbox from "~/common/NoteCheckbox";
import { getAllMarks } from "~/lib/prose/marks";
import { CustomEmoji, HardBreak, HorizontalRule, Mention, Tag, TextNode, Unstyled } from "~/lib/prose/nodes";
import { BulletPoint, NumberedListItem } from "~/lib/prose/nodes/plaintext";
import { Agenda, Goal, Todo } from "~/lib/prose/plugins/assignable";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import Indent from "~/lib/prose/plugins/indent";
import { Placeholder } from "~/lib/prose/plugins/placeholder";
import Timestamp, { withTimestampAttrs } from "~/lib/prose/plugins/timestamp/timestamp";
import ProseReact, { EditorConfig } from "~/lib/prose/prose-react";

import type { QuotedItemBlock$key } from "~/__generated__/QuotedItemBlock.graphql";

import styles from "./QuotedItem.module.css";

export function PlainTextQuotedItem({
	text,
	type = "action-item",
	done,
	streamName,
	showAsLinked = false,
}: {
	text: string;
	type: "action-item" | "talking-point";
	streamName: string;
	showAsLinked?: boolean;
	done: boolean;
	className?: string;
}) {
	return (
		<div className={styles.quotedItem}>
			<NoteCheckbox
				checkboxSize="large"
				readOnly
				state={done ? "checked" : "unchecked"}
				linked={showAsLinked}
				type={type}
				className={styles.quotedItemCheckbox}
				disabled
			/>
			<div className={styles.quotedItemText}>
				<div className={styles.quotedItemTextContent}>
					{showAsLinked && <FontAwesomeIcon icon={faLink} className={styles.quotedItemLinkIcon} fixedWidth />}
					{text}
				</div>
				<div className={styles.quotedItemTextMeta}>{streamName}</div>
			</div>
		</div>
	);
}

export default function QuotedItem({
	block: blockKey,
	className,
	showAsLinked,
	withNestedBlocks,
}: {
	block: QuotedItemBlock$key;
	className?: string;
	showAsLinked: boolean;
	withNestedBlocks: boolean;
}) {
	const block = useFragment(
		graphql`
			fragment QuotedItemBlock on NoteBlock {
				document
				descendants {
					document
				}

				note {
					id
					noteType
				}
			}
		`,
		blockKey,
	);

	const { note } = block;
	const editorConfig = React.useMemo(() => {
		const extensions = [
			...getAllMarks({ linkEditable: false, placeholderEditable: false }),
			new BlockBoundary(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new BulletPoint(),
			new NumberedListItem(),
			new HardBreak(),
			new HorizontalRule(),
			new Mention(),
			new Tag({
				showToolbar: false,
				allowEditing: false,
				clickable: false,
			}),
			new Placeholder({ showPlaceholderWhenSelectionInside: true }),
			new (withTimestampAttrs(Todo))({
				enableCopiedItemPopup: false,
				disableWhenNotEditable: true,
				disabledReadOnly: true,
				inQuotedItem: true,
			}),
			// This is a specific case where we want to provide `onTimestampClick` so that the timestamp shows, event though
			// it's not functional
			new Timestamp({ onTimestampClick: noop }),
			new Agenda({
				enableCopiedItemPopup: false,
				employeeGuidanceShowManager: false,
				disabledReadOnly: true,
				inQuotedItem: true,
			}),
			new CustomEmoji(),
			...(note.noteType == "goalset" ? [new Goal()] : []),
		];

		return new EditorConfig({
			extensions: extensions,
			nodes: {
				doc: {
					content: "block+",
				},
			},
		});
	}, [note]);

	const doc = React.useMemo(() => {
		const { schema } = editorConfig;
		const rootIndent = block.document.attrs.indent;
		const rootCopy = { ...block.document, attrs: { ...block.document.attrs, indent: 0 } };

		function createNode(nodeJson: typeof block.document) {
			const copy = { ...nodeJson, attrs: { ...nodeJson.attrs } };
			if (copy.type === "todo" && showAsLinked) {
				copy.attrs.originalBlockId = copy.attrs.blockId;
			} else {
				// AI-related attributes will be deleted, so the preview should reflect that
				delete copy.attrs.aiGenerated;
				delete copy.attrs.recording;
				delete copy.attrs.recordingOffset;
			}
			copy.attrs.indent -= rootIndent;
			return ProseNode.fromJSON(schema, copy);
		}

		return schema.nodes.doc.createChecked(undefined, [
			createNode(rootCopy),
			...(withNestedBlocks ? block.descendants.map(descendant => createNode(descendant.document)) : []),
		]);
	}, [block, editorConfig, showAsLinked, withNestedBlocks]);

	const editorContext = React.useMemo(
		() => ({
			noteId: note.id,
		}),
		[note],
	);

	return (
		<div className={cx(styles.quotedItem, className)}>
			<ProseReact
				enableReactHost
				editable={false}
				context={editorContext}
				config={editorConfig}
				controlledDoc={doc}
				className={styles.proseReact}
			/>
		</div>
	);
}
